import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'danger' | 'message' | 'profile' | 'system';
  read: boolean;
  timestamp: Date;
  time?: string;
  actionable?: boolean;
  actions?: NotificationAction[];
  url?: string; // Dynamic URL for navigation
}

export interface NotificationAction {
  label: string;
  action: () => void;
  type?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private nextId = 1;

  // Reactive signals
  notifications = signal<Notification[]>([]);
  unreadCount = computed(() => this.notifications().filter(n => !n.read).length);
  hasUnread = computed(() => this.unreadCount() > 0);

  constructor() {
    // Sync BehaviorSubject with signals
    this.notificationsSubject.subscribe(notifications => {
      this.notifications.set(notifications);
    });
  }

  /**
   * Get notifications as Observable (for async pipe)
   */
  getNotifications(): Observable<Notification[]> {
    return this.notificationsSubject.asObservable();
  }

  /**
   * Add a new notification
   */
  addNotification(
    title: string,
    message: string,
    type: Notification['type'] = 'info',
    options?: {
      actionable?: boolean;
      actions?: NotificationAction[];
      time?: string;
      url?: string;
    }
  ): string {
    const id = this.generateId();
    const notification: Notification = {
      id,
      title,
      message,
      type,
      read: false,
      timestamp: new Date(),
      time: options?.time || this.formatTimeAgo(new Date()),
      actionable: options?.actionable || false,
      actions: options?.actions || [],
      url: options?.url
    };

    const currentNotifications = this.notificationsSubject.value;
    this.notificationsSubject.next([notification, ...currentNotifications]);

    return id;
  }

  /**
   * Mark a notification as read
   */
  markAsRead(id: string): void {
    const notifications = this.notificationsSubject.value.map(notification =>
      notification.id === id ? { ...notification, read: true } : notification
    );
    this.notificationsSubject.next(notifications);
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    const notifications = this.notificationsSubject.value.map(notification => ({
      ...notification,
      read: true
    }));
    this.notificationsSubject.next(notifications);
  }

  /**
   * Remove a notification
   */
  removeNotification(id: string): void {
    const notifications = this.notificationsSubject.value.filter(n => n.id !== id);
    this.notificationsSubject.next(notifications);
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notificationsSubject.next([]);
  }

  /**
   * Get notification icon based on type
   */
  getIconForType(type: Notification['type']): string {
    const icons = {
      'info': 'bi-info-circle-fill',
      'success': 'bi-check-circle-fill',
      'warning': 'bi-exclamation-triangle-fill',
      'danger': 'bi-x-circle-fill',
      'message': 'bi-envelope-fill',
      'profile': 'bi-person-fill',
      'system': 'bi-gear-fill'
    };
    return icons[type] || icons['info'];
  }

  /**
   * Get background color class for notification type
   */
  getColorForType(type: Notification['type']): string {
    const colors = {
      'info': 'bg-primary',
      'success': 'bg-success',
      'warning': 'bg-warning',
      'danger': 'bg-danger',
      'message': 'bg-info',
      'profile': 'bg-secondary',
      'system': 'bg-dark'
    };
    return colors[type] || colors['info'];
  }

  /**
   * Add dynamic notification with URL
   */
  addDynamicNotification(
    title: string,
    description: string,
    url: string,
    type: Notification['type'] = 'info'
  ): string {
    return this.addNotification(title, description, type, { url });
  }

  /**
   * Add profile completion notification
   */
  addProfileCompletionNotification(missingFields: string[]): string {
    return this.addNotification(
      'Complete Your Profile',
      `Please complete the following fields: ${missingFields.join(', ')}`,
      'profile',
      {
        actionable: true,
        url: '/coach/coach-profile-completion',
        actions: [
          {
            label: 'Complete Profile',
            action: () => this.navigateToProfileCompletion(),
            type: 'warning'
          },
          {
            label: 'Later',
            action: () => {},
            type: 'secondary'
          }
        ]
      }
    );
  }

  /**
   * Add system notification
   */
  addSystemNotification(title: string, message: string): string {
    return this.addNotification(title, message, 'system');
  }

  /**
   * Add message notification
   */
  addMessageNotification(from: string, preview: string): string {
    return this.addNotification(
      `New message from ${from}`,
      preview,
      'message'
    );
  }

  private generateId(): string {
    return `notification_${this.nextId++}_${Date.now()}`;
  }

  private formatTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  }

  private navigateToProfileCompletion(): void {
    // This will be implemented by the component that uses this service
    // or injected as a dependency
  }
}